/**
 * Iter51 Etapa 3 — Admin: Offer + Agent Proposals review queue.
 *
 * Two tabs: pending offers / pending agents discovered at SF import time.
 * Admin can promote (creates Offer/Agent) or dismiss (mark reviewed-out).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Sparkles, Users, CheckCircle2, XCircle, RefreshCw } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProposalsAdmin = () => {
  const [activeTab, setActiveTab] = useState("offers");
  const [filterState, setFilterState] = useState("pending");
  const [offerProps, setOfferProps] = useState([]);
  const [agentProps, setAgentProps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  const [agencies, setAgencies] = useState([]);

  // Dialog state
  const [promoteDialog, setPromoteDialog] = useState({ open: false, kind: null, proposal: null });
  const [dismissDialog, setDismissDialog] = useState({ open: false, kind: null, proposal: null });
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [o, a] = await Promise.all([
        axios.get(`${API_URL}/admin/offer-proposals`, { params: { state: filterState, limit: 200 } }),
        axios.get(`${API_URL}/admin/agent-proposals`, { params: { state: filterState, limit: 200 } }),
      ]);
      setOfferProps(o.data.items || []);
      setAgentProps(a.data.items || []);
    } catch (e) {
      toast.error("Eroare la încărcarea propunerilor");
    } finally {
      setLoading(false);
    }
  }, [filterState]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Load product_types + agencies once (used in promote dialogs)
  useEffect(() => {
    (async () => {
      try {
        const r1 = await axios.get(`${API_URL}/product-types`);
        setProductTypes(Array.isArray(r1.data) ? r1.data : (r1.data?.items || []));
      } catch { /* optional */ }
      try {
        const r2 = await axios.get(`${API_URL}/agencies`);
        setAgencies(Array.isArray(r2.data) ? r2.data : (r2.data?.items || r2.data?.data || []));
      } catch { /* optional */ }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="proposals-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-amber-500" />
            Propuneri din importuri
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Oferte și agenți noi descoperiți automat la import. Promote → entitate reală în catalog.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={filterState} onValueChange={setFilterState}>
            <SelectTrigger className="w-[180px]" data-testid="filter-state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">În așteptare</SelectItem>
              <SelectItem value="promoted">Promovate</SelectItem>
              <SelectItem value="dismissed">Respinse</SelectItem>
              <SelectItem value="all">Toate</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Reîncarcă
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="offers" data-testid="tab-offers">
            Oferte ({offerProps.length})
          </TabsTrigger>
          <TabsTrigger value="agents" data-testid="tab-agents">
            Agenți ({agentProps.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="offers" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Oferte propuse</CardTitle></CardHeader>
            <CardContent className="p-0">
              {offerProps.length === 0 ? (
                <p className="p-4 text-sm text-slate-500 text-center" data-testid="empty-offers">
                  Nicio propunere {filterState !== "all" ? `(${filterState})` : ""}.
                </p>
              ) : (
                <table className="w-full text-sm" data-testid="offers-table">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                    <tr>
                      <th className="text-left p-3">Produs</th>
                      <th className="text-left p-3">Commodity</th>
                      <th className="text-left p-3">Tip înregistrare</th>
                      <th className="text-left p-3">Client</th>
                      <th className="text-left p-3">Furnizor (hint)</th>
                      <th className="text-center p-3">Văzut</th>
                      <th className="text-right p-3">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offerProps.map((p) => (
                      <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`offer-row-${p.id}`}>
                        <td className="p-3 font-medium text-slate-900">{p.product_name}</td>
                        <td className="p-3"><Badge variant="outline">{p.commodity || "—"}</Badge></td>
                        <td className="p-3 text-slate-600 text-xs">{p.registration_type || "—"}</td>
                        <td className="p-3">{p.client_type || "—"}</td>
                        <td className="p-3 text-slate-600 text-xs">{p.supplier_hint || "—"}</td>
                        <td className="p-3 text-center font-semibold text-blue-700">{p.seen_count}×</td>
                        <td className="p-3 text-right">
                          {p.state === "pending" ? (
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="outline" onClick={() => setPromoteDialog({ open: true, kind: "offer", proposal: p })} data-testid={`promote-offer-${p.id}`}>
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Promote
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setDismissDialog({ open: true, kind: "offer", proposal: p })} data-testid={`dismiss-offer-${p.id}`}>
                                <XCircle className="h-3.5 w-3.5 mr-1 text-red-600" /> Dismiss
                              </Button>
                            </div>
                          ) : (
                            <Badge variant="secondary">{p.state}</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="agents" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Agenți propuși</CardTitle></CardHeader>
            <CardContent className="p-0">
              {agentProps.length === 0 ? (
                <p className="p-4 text-sm text-slate-500 text-center" data-testid="empty-agents">
                  Nicio propunere {filterState !== "all" ? `(${filterState})` : ""}.
                </p>
              ) : (
                <table className="w-full text-sm" data-testid="agents-table">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                    <tr>
                      <th className="text-left p-3">Nume complet</th>
                      <th className="text-left p-3">Sales Unit (hint)</th>
                      <th className="text-left p-3">Cod Marca (hint)</th>
                      <th className="text-center p-3">Văzut</th>
                      <th className="text-right p-3">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentProps.map((p) => (
                      <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`agent-row-${p.id}`}>
                        <td className="p-3 font-medium text-slate-900">{p.full_name}</td>
                        <td className="p-3 text-slate-600 text-xs">{p.sales_unit_hint || "—"}</td>
                        <td className="p-3">{p.marca_code_hint || "—"}</td>
                        <td className="p-3 text-center font-semibold text-blue-700">{p.seen_count}×</td>
                        <td className="p-3 text-right">
                          {p.state === "pending" ? (
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="outline" onClick={() => setPromoteDialog({ open: true, kind: "agent", proposal: p })} data-testid={`promote-agent-${p.id}`}>
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Promote
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setDismissDialog({ open: true, kind: "agent", proposal: p })} data-testid={`dismiss-agent-${p.id}`}>
                                <XCircle className="h-3.5 w-3.5 mr-1 text-red-600" /> Dismiss
                              </Button>
                            </div>
                          ) : (
                            <Badge variant="secondary">{p.state}</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Promote dialog */}
      <PromoteDialog
        state={promoteDialog}
        onClose={() => setPromoteDialog({ open: false, kind: null, proposal: null })}
        productTypes={productTypes}
        agencies={agencies}
        submitting={submitting}
        setSubmitting={setSubmitting}
        onSuccess={fetchAll}
      />

      {/* Dismiss dialog */}
      <DismissDialog
        state={dismissDialog}
        onClose={() => setDismissDialog({ open: false, kind: null, proposal: null })}
        submitting={submitting}
        setSubmitting={setSubmitting}
        onSuccess={fetchAll}
      />
    </div>
  );
};

// ---- Promote dialog (offer or agent) ----
const PromoteDialog = ({ state, onClose, productTypes, agencies, submitting, setSubmitting, onSuccess }) => {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (state.proposal) {
      if (state.kind === "offer") {
        setForm({
          product_type_id: productTypes[0]?.id || "",
          name: state.proposal.product_name,
          code: "",
          description: state.proposal.supplier_hint || "",
          active: true,
        });
      } else {
        const parts = (state.proposal.full_name || "").split(" ");
        setForm({
          first_name: parts[0] || "",
          last_name: parts.slice(1).join(" ") || "",
          primary_marca_code: state.proposal.marca_code_hint || "",
          agency_id: "",
          active: true,
        });
      }
    }
  }, [state.proposal, state.kind, productTypes]);

  const submit = async () => {
    if (!state.proposal) return;
    setSubmitting(true);
    try {
      const url = state.kind === "offer"
        ? `${API_URL}/admin/offer-proposals/${state.proposal.id}/promote`
        : `${API_URL}/admin/agent-proposals/${state.proposal.id}/promote`;
      const payload = state.kind === "offer"
        ? form
        : { ...form, agency_id: form.agency_id || null };
      const r = await axios.post(url, payload);
      toast.success(state.kind === "offer"
        ? `Ofertă creată: ${r.data.offer_name}`
        : (r.data.attached_existing ? `Atașat la agent existent: ${r.data.agent_name}` : `Agent creat: ${r.data.agent_name}`)
      );
      onClose();
      onSuccess();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Eroare la promote"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!state.open) return null;
  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {state.kind === "offer" ? "Promote → Ofertă nouă" : "Promote → Agent nou"}
          </DialogTitle>
          <DialogDescription>
            {state.kind === "offer"
              ? "Confirmă datele pentru noua ofertă din catalog."
              : "Confirmă datele agentului. Agenția este opțională, o poți seta acum sau ulterior."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {state.kind === "offer" ? (
            <>
              <div>
                <Label>Tip produs *</Label>
                <Select value={form.product_type_id || ""} onValueChange={(v) => setForm({ ...form, product_type_id: v })}>
                  <SelectTrigger data-testid="promote-pt-select"><SelectValue placeholder="Alege..." /></SelectTrigger>
                  <SelectContent>
                    {productTypes.map((pt) => <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Denumire ofertă</Label>
                <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="promote-name-input" />
              </div>
              <div>
                <Label>Cod (auto-generat dacă gol)</Label>
                <Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="EX. PPC_ENERGIE_FIX_12" />
              </div>
              <div>
                <Label>Descriere</Label>
                <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Prenume</Label>
                  <Input value={form.first_name || ""} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="promote-firstname-input" />
                </div>
                <div>
                  <Label>Nume</Label>
                  <Input value={form.last_name || ""} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Cod Marca (opțional)</Label>
                <Input value={form.primary_marca_code || ""} onChange={(e) => setForm({ ...form, primary_marca_code: e.target.value })} />
              </div>
              <div>
                <Label>Agenție (opțional — poți seta ulterior)</Label>
                <Select value={form.agency_id || "_none"} onValueChange={(v) => setForm({ ...form, agency_id: v === "_none" ? "" : v })}>
                  <SelectTrigger data-testid="promote-agency-select"><SelectValue placeholder="Fără agenție" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Fără agenție</SelectItem>
                    {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {state.proposal?.sales_unit_hint && (
                  <p className="text-xs text-slate-500 mt-1">Hint SF: <span className="font-medium">{state.proposal.sales_unit_hint}</span></p>
                )}
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Anulează</Button>
          <Button onClick={submit} disabled={submitting || (state.kind === "offer" && !form.product_type_id)} data-testid="confirm-promote-btn">
            Confirm promote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Dismiss dialog ----
const DismissDialog = ({ state, onClose, submitting, setSubmitting, onSuccess }) => {
  const [reason, setReason] = useState("");

  useEffect(() => { setReason(""); }, [state.open]);

  const submit = async () => {
    if (!state.proposal) return;
    setSubmitting(true);
    try {
      const url = state.kind === "offer"
        ? `${API_URL}/admin/offer-proposals/${state.proposal.id}/dismiss`
        : `${API_URL}/admin/agent-proposals/${state.proposal.id}/dismiss`;
      await axios.post(url, { reason: reason.trim() || null });
      toast.success("Propunere respinsă");
      onClose();
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la dismiss");
    } finally {
      setSubmitting(false);
    }
  };

  if (!state.open) return null;
  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Respinge propunerea</DialogTitle>
          <DialogDescription>
            Propunerea va fi marcată ca respinsă și ascunsă din coada de revizuire. Decizia rămâne în log.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label>Motiv (opțional)</Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: Duplicat cu oferta X" data-testid="dismiss-reason-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Anulează</Button>
          <Button variant="destructive" onClick={submit} disabled={submitting} data-testid="confirm-dismiss-btn">Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProposalsAdmin;
